import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, useLocation } from 'react-router'
import { v4 as uuidv4 } from 'uuid'

import { SkeletonRows } from '@/components/ui/skeleton'
import { Chat } from '../../components/Chat/Chat'
import { ChatFooterComposer } from '../../components/Chat/ChatFooterComposer'
import { chatStatusFor, chatStatusHoldMs } from '../../components/Chat/chatStatus'
import { ChatStatusLine } from '../../components/Chat/ChatStatusLine'
import { Container } from '../../components/Container'
import { QuoteCartProvider } from '../../components/quotes/QuoteCartContext'
import { QuotesPageSkeleton } from '../../components/quotes/QuotesPageSkeleton'
import { useIsEmbedded } from '../../embed/child/useIsEmbedded'
import { useTimedFlag } from '../../hooks/useTimedFlag'
import { cn } from '../../lib/utils'
import { useAuthStatus } from '../../models/auth'
import { ChatItem } from '../../models/chat'
import { CONFIRM_INFO_PATH } from '../../models/confirmInfo'
import { useFeatureFlag } from '../../models/flag'
import {
  ChatActionWithValue,
  InsuranceApplicationUserIdContext,
  PortalLink,
  useInsuranceApplication,
  useInsuranceApplicationFormDataMutation,
  useInsuranceApplicationMutation,
} from '../../models/insuranceApplication'
import { InsuranceIndication } from '../../models/insuranceIndication'
import { isOrgAdmin, type Org, showsQuotesPage, useCurrentUser } from '../../models/user'
import { ApplicationChatLayout } from './InsuranceApplication/ApplicationChatLayout'
import { ChatEndedNotice } from './InsuranceApplication/ChatEndedNotice'
import { InsuranceApplicationDevTools } from './InsuranceApplication/InsuranceApplicationDevTools'
import { InsuranceApplicationNav } from './InsuranceApplication/InsuranceApplicationNav'
import { InsuranceQuotesPage } from './InsuranceApplication/InsuranceQuotesPage'
import { useCustomerApplication } from './InsuranceApplication/useCustomerApplication'

interface InsuranceApplicationViewProps {
  applicationId: string
  userId?: string
  /** Swaps the chat for the quotes page once carriers have prices; unset keeps the transcript. */
  quotesPage?: boolean
  /** Forces the quotes page read-only; left unset, the fleet's status decides. */
  readonly?: boolean
  /** Puts the cart opener in the quotes page's summary row — for layouts whose nav has no room for one. */
  showCartButton?: boolean
  /**
   * Marks a staff board: the quotes page reads the declines the customer's overlay covers, and the
   * chat closes with a notice once the customer has moved on to their quotes page.
   */
  staffView?: boolean
  /** The application's own org — staff views pass the customer's, not the signed-in staffer's. */
  org?: Org
  /** A held status line is up for this long; the page keeps the customer here until it is done. */
  onStatusHold?: (ms: number) => void
}

export function InsuranceApplicationView({
  applicationId,
  userId,
  quotesPage,
  readonly,
  showCartButton,
  staffView,
  org,
  onStatusHold,
}: InsuranceApplicationViewProps) {
  return (
    <InsuranceApplicationUserIdContext.Provider value={userId}>
      <InsuranceApplicationViewInner
        applicationId={applicationId}
        quotesPage={quotesPage}
        readonly={readonly}
        showCartButton={showCartButton}
        staffView={staffView}
        org={org}
        onStatusHold={onStatusHold}
      />
    </InsuranceApplicationUserIdContext.Provider>
  )
}

function InsuranceApplicationViewInner({
  applicationId,
  quotesPage,
  readonly,
  showCartButton,
  staffView,
  org: orgProp,
  onStatusHold,
}: {
  applicationId: string
  quotesPage?: boolean
  readonly?: boolean
  showCartButton?: boolean
  staffView?: boolean
  org?: Org
  onStatusHold?: (ms: number) => void
}) {
  const [sidebar, setSidebar] = useState<React.ReactNode>(null)
  const { data: authStatus } = useAuthStatus()
  const { data: application, refetch } = useInsuranceApplication({
    id: applicationId,
    enabled: Boolean(authStatus?.authenticated),
  })
  const { mutate, variables, isPending } = useInsuranceApplicationMutation({
    application: application!,
    onSettled: onMessageSent,
  })
  const {
    mutate: mutateFormData,
    variables: variablesFormData,
    isPending: isPendingFormData,
  } = useInsuranceApplicationFormDataMutation({
    application: application!,
    onSettled: onMessageSent,
  })
  const hideAskQuestions = useFeatureFlag('HIDE_ASK_QUESTIONS')
  const { user } = useCurrentUser({ enabled: false })
  // Staff views pass the customer's org; on the customer's own routes it is theirs.
  const org = orgProp ?? user?.currentOrg

  const isExpanded = Boolean(sidebar)

  function getPendingMessages(): ChatItem[] {
    if (variables && isPending && variables.content) {
      const data = [
        {
          id: '',
          clientId: variables.clientId,
          dateCreated: new Date().toISOString(),
          dateUpdated: new Date().toISOString(),
          isFromUser: true,
          content: variables.content,
          metadata: {
            service: variables.service || application?.action.service,
          },
        },
      ]

      return data
    }

    if (variablesFormData && variablesFormData && isPendingFormData) {
      return [
        {
          id: '',
          clientId: variablesFormData.get('client_id') as string,
          dateCreated: new Date().toISOString(),
          dateUpdated: new Date().toISOString(),
          isFromUser: true,
          content: {
            component: 'P',
            content: variablesFormData.get('content') as string,
            props: {},
          },
        },
      ]
    }

    return []
  }

  const pendingMessagesRaw = getPendingMessages()
  const pendingMessagesKey = JSON.stringify(pendingMessagesRaw)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pendingMessages = useMemo(() => pendingMessagesRaw, [pendingMessagesKey])

  // Stabilize action reference so polling refetches don't re-render interactive components
  const actionJson = JSON.stringify(application?.action)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableAction = useMemo(() => application?.action, [actionJson])

  // The step just answered, so the status line can say what Cai is doing with it.
  const [sentService, setSentService] = useState<string>()
  const [statusHeld, holdStatus] = useTimedFlag()

  // Stabilize sendMessage so it doesn't cause re-renders downstream
  const sendMessageRef = useRef<(data: ChatActionWithValue | FormData) => Promise<void> | null>(null)
  sendMessageRef.current = async (data: ChatActionWithValue | FormData) => {
    const service =
      data instanceof FormData ? application!.action.service : data.service || application!.action.service
    setSentService(service)
    const holdMs = chatStatusHoldMs(service)
    if (holdMs) {
      holdStatus(holdMs)
      onStatusHold?.(holdMs)
    }
    if (data instanceof FormData) {
      data.append('client_id', uuidv4())
      data.append('service', application!.action.service)
      await mutateFormData(data)
    } else {
      await mutate({
        clientId: uuidv4(),
        content: data.content,
        value: data.value,
        service: data.service,
        documentIds: data.documentIds,
        attestations: data.attestations,
        bind_attestations: data.bind_attestations,
        dom_html: data.dom_html,
        dom_screenshot_b64: data.dom_screenshot_b64,
        client_context: data.client_context,
      })
    }
  }
  const sendMessage = useCallback((data: ChatActionWithValue | FormData) => {
    return sendMessageRef.current!(data)
  }, [])

  useEffect(() => {
    if (isPending || isPendingFormData) return
    refetch()
    const interval = setInterval(refetch, 4000)
    return () => clearInterval(interval)
  }, [refetch, isPending, isPendingFormData])

  async function onMessageSent() {
    await new Promise((resolve) => setTimeout(resolve, 1000))
    await refetch()
  }

  if (!application) {
    return quotesPage ? (
      <QuotesPageSkeleton />
    ) : (
      <SkeletonRows className="mx-auto mt-10 w-full max-w-130 px-4" rows={6} rowClassName="h-16" />
    )
  }

  const customerOnQuotes = showsQuotesPage(org, application)
  const showQuotesPage = !!quotesPage && customerOnQuotes
  // The customer can't come back to this chat, so it takes no more questions.
  const chatEnded = !!staffView && customerOnQuotes
  const chatItems = application.chat?.items || []
  // Answered and not yet replied to: the same moment the typing dots show.
  const working = isPending || isPendingFormData || !!chatItems[chatItems.length - 1]?.isFromUser

  return (
    <InsuranceApplicationSidebarContext.Provider
      value={{
        sidebar,
        setSidebar,
        isExpanded,
        portalLinks: application.portalLinks,
        applicationId: application.id,
        indications: application.indications,
        applicationEditedAt: application.dateLastEdited,
      }}
    >
      {showQuotesPage ? (
        <InsuranceQuotesPage
          application={application}
          onEditSuccess={refetch}
          readonly={readonly}
          showCartButton={showCartButton}
          staffView={staffView}
          org={org}
        />
      ) : (
        <ApplicationChatLayout
          steps={application.progress}
          percentComplete={application.percentComplete}
          sidebarOpen={isExpanded}
          staffView={staffView}
          status={<ChatStatusLine text={working || statusHeld ? chatStatusFor(sentService) : undefined} />}
          footer={
            <ChatFooterComposer
              action={stableAction}
              sendMessage={sendMessage}
              pending={isPending || isPendingFormData}
            />
          }
        >
          <Chat
            messages={chatItems}
            action={stableAction!}
            pendingMessages={pendingMessages}
            sendMessage={sendMessage}
            hideAskQuestions={hideAskQuestions || chatEnded}
            application={application}
            onEditSuccess={refetch}
            orgId={application.orgId}
          />
          {chatEnded && <ChatEndedNotice orgId={application.orgId} />}
        </ApplicationChatLayout>
      )}
      {/* Outside the layout: its scroller's fade mask would clip the fixed sheet. */}
      {!showQuotesPage && sidebar}

      <InsuranceApplicationDevTools application={application} />
    </InsuranceApplicationSidebarContext.Provider>
  )
}

/** The application chat. Once carriers have been asked for prices the customer belongs on /quotes. */
export function InsuranceApplication() {
  const { user, application, isLoading, showsQuotes, reviewsOnPage } = useCustomerApplication()
  const [redirectHeld, holdRedirect] = useTimedFlag()
  const { search, hash } = useLocation()
  const isEmbed = useIsEmbedded()

  // The nav reads the cart, though only /quotes fills it.
  return (
    <QuoteCartProvider>
      {/* One screen tall, so the chat scrolls in its column; an embed sizes to its content instead. */}
      <div className={cn('flex flex-col', isEmbed ? 'flex-1' : 'h-dvh')}>
        <InsuranceApplicationNav />
        {!user || isLoading ? (
          <Container className="mt-10">
            <SkeletonRows className="mt-10" rows={6} rowClassName="h-16" />
          </Container>
        ) : !isOrgAdmin(user) || !application ? (
          <Navigate to="/insure/insurance" />
        ) : showsQuotes ? (
          // The query rides along: ad clicks land here carrying click ids like _gl.
          <Navigate to={{ pathname: '/quotes', search, hash }} replace />
        ) : reviewsOnPage && !redirectHeld ? (
          <Navigate to={{ pathname: CONFIRM_INFO_PATH, search, hash }} replace />
        ) : (
          <>
            <Helmet>
              <title>Insurance Application</title>
            </Helmet>
            <InsuranceApplicationView applicationId={application.id} onStatusHold={holdRedirect} />
          </>
        )}
      </div>
    </QuoteCartProvider>
  )
}

interface InsuranceApplicationSidebarContextType {
  isExpanded: boolean
  sidebar: React.ReactNode
  setSidebar: (sidebar: React.ReactNode) => void
  portalLinks?: PortalLink[]
  applicationId?: string
  indications?: InsuranceIndication[]
  applicationEditedAt?: string | null
}

export const InsuranceApplicationSidebarContext = createContext<InsuranceApplicationSidebarContextType>({
  isExpanded: false,
  sidebar: null,
  setSidebar: () => null,
  portalLinks: undefined,
  applicationId: undefined,
  indications: undefined,
})

export const useInsuranceApplicationSidebarContext = () => {
  return useContext(InsuranceApplicationSidebarContext)
}
